// slotPane — resolve which PageSlot PANE an interaction originated in, so
// every slot opener can target that pane's OWN state key (split-view fix,
// 2026-08-06 per Nika "fix bug when its on split view - it often changes both
// views on navigation click").
//
// canvas/components/PageSlot.js stamps `_slotPaneKey` on each pane element
// AND on every keep-alive host it mounts a page into ('_canvasSlot' for the
// primary, '_canvasSlotSecond' for the split second). Walking the element
// parent chain from a click's origin therefore answers "which pane am I
// inside?" without any opener needing PageSlot internals — outside a pane
// (navbar, launcher, notifications, toasts) the walk falls through to the
// primary, which is the long-standing default every opener already had.
//
// Lives in shared/functions for the same reason openInSlot/minimizedSlots do:
// PageSlot ships in the canvas package while openAppPage lives in the
// workspace package, and shared is the one package both link. Imported by
// DIRECT path (the workspacePrefix.js idiom) — package-internal plumbing, not
// an el.call surface.

export const SLOT_PANE_PRIMARY = '_canvasSlot'
export const SLOT_PANE_SECOND = '_canvasSlotSecond'

// The nearest enclosing pane's slot state key, else the primary's. Hop-capped
// so a malformed/cyclic parent chain can never hang a click handler.
export const slotPaneKeyFor = (el) => {
  let node = el
  let hops = 0
  while (node && hops < 100) {
    const key = node._slotPaneKey
    if (key === SLOT_PANE_PRIMARY || key === SLOT_PANE_SECOND) return key
    if (node.parent === node) break
    node = node.parent
    hops++
  }
  return SLOT_PANE_PRIMARY
}

// The nearest enclosing pane's slot state key, or `null` when the caller is
// NOT inside a pane at all (navbar, launcher, notifications, a toast, the
// command palette). `slotPaneKeyFor` above cannot express that: its
// fall-through to the primary is a deliberate DEFAULT, so "the click came
// from the primary pane" and "the click came from no pane" both read as
// `_canvasSlot`. A caller that has to branch on the difference — e.g. an
// opener that targets the origin pane when there is one and opens a fresh
// slot otherwise — needs the null.
//
// Self-contained walk rather than a shared module-local helper: this package
// already carries that rule verbatim in functions/themeMode.js (a sibling
// export read bare by another export is unresolvable once the file's exports
// are individually frank.toJSON'd — project_frank_drops_module_local_helpers).
// Same hop cap, same stamp validation, same cyclic-chain guard as
// `slotPaneKeyFor`; ONLY the fall-through differs.
export const slotPaneKeyForStrict = (el) => {
  let node = el
  let hops = 0
  while (node && hops < 100) {
    const key = node._slotPaneKey
    if (key === '_canvasSlot' || key === '_canvasSlotSecond') return key
    if (node.parent === node) break
    node = node.parent
    hops++
  }
  return null
}

// Does the caller render under a PageSlot TITLE BAR? — `el.call(
// 'paneTitleBarShown', s)`, bound to the element.
//
// One title per host (design-direction.md §3). At its dedicated URL a page's
// own title is the only one on screen. In a pane the bar above it (PageSlot
// `Header.Title`, the pane's `h1` and its `aria-labelledby` target) already
// names the page, so a page title that repeats it yields: `hide:` for a plain
// title, a `tag:` factory for a heading that says something else. Measured
// live 2026-09-21: six surfaces painted the same words twice, 25px apart, as
// two `h1`s.
//
// FALSE in exactly the two cases where the page's own title is the only one:
// outside a pane, and in an IMMERSIVE module pane — PageSlot hides its header
// there (see `Header.hide`), unless a module rail keeps it. The two module
// functions live in the workspace shell package, so they are feature-tested
// off the context the way PageSlot itself reads them; a bundle without them
// (canvas / marketplace standalone) has no immersive mode to ask about.
// canvas/__tests__/pageSlotTitleBarParity.test.js holds this and PageSlot's
// own `Header.hide` to the same truth table.
//
// `slotPaneKeyForStrict` is reached through `this.call`, never bare: a sibling
// export read bare is unresolvable once exports are individually serialized
// (the rule this file already states above).
export function paneTitleBarShown(state) {
  if (!this.call('slotPaneKeyForStrict', this)) return false
  const functions = (this.context && this.context.functions) || {}
  const root = (state && state.root) || (this.state && this.state.root)
  if (typeof functions.moduleRailActive === 'function' && functions.moduleRailActive(root))
    return true
  return !(
    typeof functions.moduleImmersiveActive === 'function' && functions.moduleImmersiveActive(root)
  )
}

// The heading LEVEL of a page title — `tag: (el, s) => el.call('pageTitleTag', s)`.
//
// The other half of "one title per host". A page title is the page's one `h1`
// at its dedicated URL. Under a pane title bar the bar is already the `h1`, so
// a title that stays on screen there (it says more than the bar) is an `h2`. A
// title that REPEATS the bar hides instead (`paneTitleBarShown`), and its level
// then does not matter.
//
// Why this exists (live walk 2026-09-21): 22 operator pages, the three
// ContentPage pages and the documents and meet headers shipped their title as
// a bare `div` or an `h2`, so their dedicated URL had no `h1` at all. A literal
// `tag: 'h1'` would have fixed that and put a second `h1` into every pane.
//
// ALWAYS answers a heading tag. A `tag:` factory that answers nothing makes the
// framework infer the tag from the KEY, and `Title` is a structural tag it
// refuses — the node would silently become a `div` (element/src/cache.js).
//
// The framework resolves a `tag:` factory ONCE, at create. A page instance
// never moves between hosts (the pane mounts its own), so the level is right
// for the life of the node.
export function pageTitleTag(state) {
  return this.call('paneTitleBarShown', state) ? 'h2' : 'h1'
}
